import { useMemo } from 'react'
import { isMobile, isTablet } from 'mobile-device-detect'
import { useWindowDimensions } from './useWindowDimensions'

// Breakpoint constants
export const BREAKPOINTS = {
  PHONE_MAX: 767,
  TABLET_MIN: 768,
  TABLET_MAX: 1023,
  DESKTOP_MIN: 1024,
} as const

// Layout dimension constants
export const LAYOUT_DIMENSIONS = {
  BOTTOM_TAB_HEIGHT: 56,
  SIDE_RAIL_WIDTH: 80,
  MOBILE_DRAWER_WIDTH: 320,
  MOBILE_DRAWER_MAX_WIDTH: 400,
  FAB_SIZE: 56,
  FAB_MARGIN: 16,
  SAFE_AREA_BOTTOM: 34, // For notched devices
} as const

export interface ResponsiveLayout {
  // Device type (based on mobile-device-detect + width)
  isPhone: boolean
  isTablet: boolean
  isDesktop: boolean

  // Orientation
  isPortrait: boolean
  isLandscape: boolean

  // Combined layout modes
  isPhonePortrait: boolean
  isPhoneLandscape: boolean
  isTabletPortrait: boolean
  isTabletLandscape: boolean

  // Layout visibility flags
  showBottomTabs: boolean
  showSideRail: boolean
  showHeaderNav: boolean
  showPersistentSidebar: boolean
  showDeviceFAB: boolean

  // Dimensions
  width: number
  height: number

  // Computed paddings
  mainPaddingBottom: number
  mainPaddingRight: number
  mainPaddingLeft: number
  fabBottomPosition: number
}

export function useResponsiveLayout(): ResponsiveLayout {
  const { width, height } = useWindowDimensions()

  return useMemo(() => {
    // Device detection using mobile-device-detect library + width fallback
    // This handles cases where device detection may not be accurate
    const detectedPhone = isMobile && !isTablet
    const detectedTablet = isTablet

    // Width-based fallback for SSR or when detection fails
    const isPhone = detectedPhone || (!isTablet && width <= BREAKPOINTS.PHONE_MAX)
    const isTabletDevice = detectedTablet || (width >= BREAKPOINTS.TABLET_MIN && width <= BREAKPOINTS.TABLET_MAX && !detectedPhone)
    const isDesktop = !isPhone && !isTabletDevice && width >= BREAKPOINTS.DESKTOP_MIN

    // Orientation detection
    const isPortrait = height > width
    const isLandscape = width >= height

    // Combined layout modes
    const isPhonePortrait = isPhone && isPortrait
    const isPhoneLandscape = isPhone && isLandscape
    const isTabletPortrait = isTabletDevice && isPortrait
    const isTabletLandscape = isTabletDevice && isLandscape

    // Layout visibility flags
    // Bottom tabs: Phone portrait, Tablet portrait
    const showBottomTabs = isPhonePortrait || isTabletPortrait

    // Side rail (right edge): Phone landscape only
    const showSideRail = isPhoneLandscape

    // Header nav: Desktop, Tablet landscape
    const showHeaderNav = isDesktop || isTabletLandscape

    // Persistent sidebar: Tablet landscape, Desktop
    const showPersistentSidebar = isTabletLandscape || isDesktop

    // Device FAB: Phone (both orientations), Tablet portrait
    const showDeviceFAB = isPhone || isTabletPortrait

    // Compute paddings for main content area
    let mainPaddingBottom = 0
    let mainPaddingRight = 0
    let mainPaddingLeft = 0

    if (showBottomTabs) {
      // Add space for bottom tabs + safe area
      mainPaddingBottom = LAYOUT_DIMENSIONS.BOTTOM_TAB_HEIGHT
    }

    if (showSideRail) {
      // Add space for side rail on right
      mainPaddingRight = LAYOUT_DIMENSIONS.SIDE_RAIL_WIDTH
    }

    // FAB position (above bottom tabs if present, otherwise at bottom)
    const fabBottomPosition = showBottomTabs
      ? LAYOUT_DIMENSIONS.BOTTOM_TAB_HEIGHT + LAYOUT_DIMENSIONS.FAB_MARGIN
      : LAYOUT_DIMENSIONS.FAB_MARGIN

    return {
      // Device type
      isPhone,
      isTablet: isTabletDevice,
      isDesktop,

      // Orientation
      isPortrait,
      isLandscape,

      // Combined layout modes
      isPhonePortrait,
      isPhoneLandscape,
      isTabletPortrait,
      isTabletLandscape,

      // Layout visibility flags
      showBottomTabs,
      showSideRail,
      showHeaderNav,
      showPersistentSidebar,
      showDeviceFAB,

      // Dimensions
      width,
      height,

      // Computed paddings
      mainPaddingBottom,
      mainPaddingRight,
      mainPaddingLeft,
      fabBottomPosition,
    }
  }, [width, height])
}
